import styles from "./grocery-budget.module.css"

import React from "react"
import cx from "classnames"
import {Wizard} from "activities/components/wizard"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import * as str from "shared/string"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as env from "util/env"
import * as callouts from "activities/components/callouts"
import {getGroceryBudget} from "story/util/grocery-budget"
import {RadioMenu, RadioOption} from "shared/components/inputs"

const HouseholdSize = ({householdSize, updateState, resetKey, step}) => {
  const question = "How many people are in your household?"
  return (
    <div className={styles["household-size-page"]}>
      <inputs.Label inputId="household-size" customStyles={styles["label"]}>
        {question}
      </inputs.Label>
      <inputs.LongCalcInput
        id="household-size"
        resetKey={resetKey}
        className={styles["long-calc-input"]}
        onChange={updateState("householdSize")}
        customMask="number"
        size="long"
        placeholder="Enter a number"
        value={householdSize}
      />
    </div>
  )
}

const HouseholdDetails = (props) => {
  const {householdSize, people, updateState, resetKey, step} = props

  const updatePerson = (i, key, newVal) => ({
    ...people,
    [i]: {...people[i], [key]: parseInt(newVal)}
  })

  return (
    <div className={styles["person-list"]}>
      {[...Array(parseInt(householdSize)).keys()].map((i) => (
        <div key={i}>
          <div className={styles["person-label"]}>Person {i + 1}</div>
          <div className={styles["person-inputs"]}>
            <inputs.Input id="person" title="Age" valueType="number" onChange={updateState("people", (v) => updatePerson(i, "age", v))} />
            <inputs.Input id="person" title="Meals Out Per Week" valueType="number" onChange={updateState("people", (v) => updatePerson(i, "mealsOut", v))} />
          </div>
        </div>
      ))}
    </div>
  )
}

const BudgetResults = ({budgetingType, people, updateState, resetKey, step}) => {
  const peopleValues = Object.values(people)
  const householdSize = peopleValues.length
  const budget = getGroceryBudget({
    plan: "all",
    members: peopleValues,
    timePeriod: "weekly"
  })

  const question = "How will you budget?"
  const typeOptions = [
    ["thrifty", "Thrifty"],
    ["low", "Low Cost"],
    ["moderate", "Moderate"],
    ["liberal", "Generous"]
  ]

  const weeklyBudget = budget[budgetingType]
  const perPersonPerMealCost = weeklyBudget / householdSize / 21
  const totalMealsOut = peopleValues.reduce((acc, person) => acc + person.mealsOut, 0)

  const weeklyBudgetWithoutMealsOut = weeklyBudget - totalMealsOut * perPersonPerMealCost

  const Label = (props) => (
    <inputs.Label customStyles={styles["label"]} {...props}>
      {question}
    </inputs.Label>
  )
  return (
    <div className={styles["results-page"]}>
      <div>
        <div className={styles["dropdown"]}>
          <Label inputId="budgeting-type-select" />
          <inputs.Dropdown
            id="budgeting-type"
            selectClassName={styles["select"]}
            arrowIcon="filled-down-arrow"
            options={typeOptions}
            initialValue={budgetingType}
            onChange={updateState("budgetingType")}
            backgroundClassName={styles["dropdown-background"]}
          />
        </div>
        <div className={styles["radio-wrapper"]}>
          <Label inputId="budgeting-type-radio" />
          <RadioMenu id="budgeting-type-radio" className={styles["radio-menu"]} onChange={updateState("budgetingType")} value={budgetingType}>
            {typeOptions.map(([value, label]) => (
              <RadioOption value={value} font={fonts.getFontClass()}>
                {label}
              </RadioOption>
            ))}
          </RadioMenu>
        </div>
      </div>
      <div className={styles["callout-container"]}>
        <div className={styles["callout-title"]}>Your Household Grocery Budget</div>
        <callouts.Callout calloutStyles={styles["callout"]}>
          <callouts.Detail
            labelStyles={styles["callout-label"]}
            statStyles={styles["stat"]}
            resultStyles={styles["result"]}
            label="Weekly"
            result={util.formatDollars(weeklyBudgetWithoutMealsOut)}
          />
          <callouts.Detail
            labelStyles={styles["callout-label"]}
            statStyles={styles["stat"]}
            resultStyles={styles["result"]}
            label="Monthly"
            result={util.formatDollars(weeklyBudgetWithoutMealsOut * 4.333)}
          />
        </callouts.Callout>
      </div>
    </div>
  )
}

const GroceryBudget_ = shared.ActivityFactory(
  ({getProps, getState, updateState, setState, onReset}) => {
    const {resetKey, householdSize, people, budgetingType} = getState()
    const {steps, theme} = getProps()
    return (
      <Wizard
        className={styles["grocery-budget-calculator"]}
        resetKey={resetKey}
        onReset={onReset}
        setup={{
          steps: steps,
          theme: theme,
          props: {
            householdSize,
            people,
            budgetingType,
            theme: theme,
            setState: setState,
            updateState: updateState
          }
        }}
      />
    )
  },
  {
    defaultProps: {
      className: styles["grocery-budget-calculator"],
      steps: [
        {
          title: "Grocery Budget Calculator",
          subtitle: "Enter details about the people in your household.",
          isStepComplete: (stepProps) => stepProps.householdSize > 0,
          onChangeStep: (setState, stepProps) => setState({householdSize: stepProps.householdSize}),
          content: HouseholdSize,
          buttonText: "Next"
        },
        {
          title: "Grocery Budget Calculator",
          subtitle: "Enter details about the people in your household.",
          isStepComplete: (stepProps) => {
            const people = Object.values(stepProps.people)
            return Object.values(people).length == stepProps.householdSize && people.every((person) => Number.isInteger(person.age) && Number.isInteger(person.mealsOut))
          },
          onChangeStep: (setState, stepProps) => setState({people: stepProps.people}),
          content: HouseholdDetails,
          buttonText: "Next"
        },
        {
          title: "Grocery Budget Calculator",
          subtitle: "Let’s take a look at the results.",
          isStepComplete: () => true,
          content: BudgetResults,
          buttonText: "Start Over"
        }
      ]
    },
    stateFilter: (state) => state,
    defaultState: {
      householdSize: 0,
      people: {},
      budgetingType: "thrifty"
    }
  }
)

export default (props) => <GroceryBudget_ {...props} />
